import api from "./api";

// System-user (back-office operator) management. RBAC-gated on the backend;
// every call here requires a valid bearer token (handled by the api instance).
//
// Response-shape note: the backend wraps single-record writes inconsistently —
// index/permissions return a plain array, show returns [obj], and
// store/update/updateStatus return [[ {message, ...obj} ]]. `unwrap()` peels any
// nesting of single-element arrays so callers always get a plain object.
const unwrap = (data) => {
  let v = data;
  while (Array.isArray(v) && v.length === 1) v = v[0];
  return v;
};

// GET /api/system/users — optional { search, role_id, is_active } filters.
// Returns a plain array of users: {id,name,email,role_id,role,is_active,...}.
export const listSystemUsers = (params = {}) =>
  api
    .get("/api/system/users", { params })
    .then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/system/users/{id}
export const getSystemUser = (id) =>
  api.get(`/api/system/users/${id}`).then((r) => unwrap(r.data));

// POST /api/system/users  { name, employee_no, email, role_id, is_active? }
// employee_no is required and must be a unique integer. No `password` field —
// the backend generates a temporary one, emails it to the user, and sets
// must_change_password: true on the new account. Returns the created user
// (with an extra `message` field).
export const createSystemUser = (payload) =>
  api.post("/api/system/users", payload).then((r) => unwrap(r.data));

// PUT /api/system/users/{id}  partial: { name?, employee_no?, email?, role_id?, is_active? }
export const updateSystemUser = (id, payload) =>
  api.put(`/api/system/users/${id}`, payload).then((r) => unwrap(r.data));

// PATCH /api/system/users/{id}/status  { is_active }
export const setSystemUserStatus = (id, isActive) =>
  api
    .patch(`/api/system/users/${id}/status`, { is_active: isActive })
    .then((r) => unwrap(r.data));

// POST /api/system/users/{id}/reset-password — no body. The backend generates
// a new temporary password, emails it to the user, and sets
// must_change_password: true. -> { message, temporary_password_email_sent }
export const resetSystemUserPassword = (id) =>
  api.post(`/api/system/users/${id}/reset-password`).then((r) => r.data);

// GET /api/system/login-logs — gated by middleware('role:admin') directly, not
// a permission check (see adminNav.js "login-logs" entry). Optional
// { search, user_id, from_date, to_date } filters. Returns a plain array:
// {id,user_id,user_name,user_email,employee_no,logged_in_at,ip_address,user_agent}.
export const listSystemUserLoginLogs = (params = {}) =>
  api
    .get("/api/system/login-logs", { params })
    .then((r) => (Array.isArray(r.data) ? r.data : []));
